// CardDoClube.js — o card de livro do acervo, na página do Clube.
//
// É o CardDestaque (o card único do site) com a parte de VENDA tirada: sem
// preço, sem carrinho, sem coração da "Minha Lista" e sem a fita da curadoria.
// Sobra o que o pedido listou — capa, título, autores e avaliação —, com o mesmo
// acabamento de sombra dura, para o assinante reconhecer o livro como o mesmo
// que ele vê em /livros.
//
// POR QUE UM COMPONENTE À PARTE, e não uma prop `semPreco` no CardDestaque:
// aquele card existe em torno do preço (a linha reservada do riscado, a do
// impresso, o botão pendurado na borda e o `gap-y-12` que a grade precisa por
// causa dele). Uma prop que desliga metade dele deixaria as alturas reservadas
// sem motivo, e cada correção futura no card de venda teria de lembrar do caso
// que não vende.
//
// O LINK VAI PARA A VITRINE DO CLUBE (/clube/livros/:id), não para /livros/:id:
// a página do livro na loja é uma página de compra, e o assinante que abre um
// livro do acervo quer ler.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import NotaLivro from "../produto/paginaProduto/NotaLivro";

const API_URL = process.env.REACT_APP_API;
const main = process.env.REACT_APP_MAIN;

/** Todos os autores, por nome. Ignora `null` — há livro com `autores: [null]`. */
export const nomesDosAutores = (autores) =>
  (Array.isArray(autores) ? autores : [])
    .filter((a) => a != null)
    .map((a) => (typeof a === "object" ? a.nome : a))
    .filter(Boolean);

const CardDoClube = ({ livro, onAbrir }) => {
  const [hover, setHover] = useState(false);
  const [capaCarregada, setCapaCarregada] = useState(false);

  const autores = nomesDosAutores(livro.autores);

  return (
    <Link
      to={main + "clube/livros/" + livro.id}
      onClick={onAbrir}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className={
        "card-livro block relative h-full rounded-card p-3 pb-4 border-2 border-insigne-sombra " +
        "bg-insigne-navy-soft transition-[transform,box-shadow] duration-200 will-change-transform " +
        (hover ? "-translate-x-[3px] -translate-y-[3px]" : "")
      }
      style={{ boxShadow: `${hover ? 7 : 4}px ${hover ? 7 : 4}px 0 0 rgb(var(--c-sombra))` }}
    >
      <div className="relative overflow-hidden rounded-card border-2 border-insigne-sombra bg-insigne-navy-deep aspect-[3/4]">
        {!capaCarregada && <div className="absolute inset-0 animate-pulse bg-insigne-navy-mid/60" />}
        <img
          src={`${API_URL}public/${livro.image}`}
          alt={livro.titulo}
          loading="lazy"
          onLoad={() => setCapaCarregada(true)}
          onError={() => setCapaCarregada(true)}
          className={
            "relative w-full h-full object-cover transition-transform duration-500 ease-out " +
            (hover ? "scale-[1.05]" : "scale-100") +
            (capaCarregada ? " opacity-100" : " opacity-0")
          }
        />
      </div>

      <div className="mt-3.5 px-1">
        {/* Altura FIXA de duas linhas, como no card da loja: um título curto e
            um longo na mesma fileira não podem empurrar as estrelas para
            alturas diferentes. */}
        <h3 className="text-white font-bold leading-snug text-sm md:text-base h-10 md:h-11 line-clamp-2 overflow-hidden">
          {livro.titulo}
        </h3>
        {/* TODOS os autores, não só o primeiro: sem preço no card, esta é a
            segunda informação mais importante dele, e obra coletiva é comum no
            acervo. Uma linha só, cortada com reticências; o nome inteiro fica
            no `title`. */}
        <p className="text-white/55 text-xs mt-1 h-4 truncate" title={autores.join(", ")}>
          {autores.join(", ")}
        </p>
        <div className="h-5 flex items-center mt-1.5">
          <NotaLivro livro={livro} size="text-[11px]" compacto />
        </div>
      </div>
    </Link>
  );
};

/** O esqueleto com as mesmas alturas do card, para a grade não pular ao chegar. */
export const CardDoClubeSkeleton = () => (
  <div className="p-3 animate-pulse" aria-hidden="true">
    <div className="rounded-lg bg-insigne-navy-soft aspect-[3/4]" />
    <div className="mt-3.5 px-1">
      <div className="h-4 rounded bg-insigne-navy-soft" />
      <div className="h-4 w-2/3 rounded bg-insigne-navy-soft mt-2" />
      <div className="h-3 w-1/2 rounded bg-insigne-navy-soft mt-3" />
      <div className="h-3 w-20 rounded bg-insigne-navy-soft mt-3" />
    </div>
  </div>
);

export default CardDoClube;
